import emailjs from "@emailjs/browser";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface SendEmailOptions {
  formData: FormData;
  setFormData: (data: FormData) => void;
  successIcon: JSX.Element;
  errorIcon: JSX.Element;
}

export const sendEmail = async ({
  formData,
  setFormData,
  successIcon,
  errorIcon
}: SendEmailOptions) => {
  if (!formData.name || !formData.email || !formData.message) {
    toast.error("All fields are required.", {
      className: "custom-toast-class",
      bodyClassName: "custom-toast-body",
      icon: errorIcon,
    });
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.email)) {
    toast.error("Please enter a valid email address.", {
      className: "custom-toast-class",
      bodyClassName: "custom-toast-body",
      icon: errorIcon,
    });
    return;
  }

  // 1) Save the submission to our Google Sheet "box" (best-effort, fire-and-forget).
  //    Uses no-cors so the browser can post to the Apps Script web app without a preflight.
  let saved = false;
  const leadsWebhook = process.env.REACT_APP_LEADS_WEBHOOK_URL;
  if (leadsWebhook) {
    try {
      await fetch(leadsWebhook, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          source: "share-your-pattern",
          createdAt: new Date().toISOString(),
        }),
      });
      saved = true; // no-cors response is opaque, but the request was sent
    } catch (error) {
      console.error("Failed to save lead to sheet:", error);
    }
  }

  // 2) Still send the email notification (best-effort).
  let emailed = false;
  try {
    await emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID || "",
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "",
      {
        Name: formData.name,
        Email: formData.email,
        Information: formData.message,
      },
      process.env.REACT_APP_EMAILJS_USER_ID || ""
    );
    emailed = true;
  } catch (error) {
    console.error("Failed to send email:", error);
  }

  // Success if the submission was captured by either channel.
  if (saved || emailed) {
    toast.success("Thank you for sharing! Your email has been successfully sent!", {
      className: "custom-toast-class",
      bodyClassName: "custom-toast-body",
      icon: successIcon,
    });
    setFormData({ name: "", email: "", message: "" });
  } else {
    toast.error("Oops! Something went wrong, please try again.", {
      className: "custom-toast-class",
      bodyClassName: "custom-toast-body",
      icon: errorIcon,
    });
  }
};
